import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SubNav } from "@/components/case-study/CaseChrome";
import Footer from "@/components/Footer";

const FC = "/case-studies/fishcaddy";
const SCREENS = {
  homepage: `${FC}/homepage.png`,
  onboarding: `${FC}/onboarding.png`,
  kern: `${FC}/location-kern.png`,
  sonoma: `${FC}/location-sonoma.png`,
  newsHero: `${FC}/newsletter-hero.png`,
  newsReports: `${FC}/newsletter-reports.png`,
};
const SCREEN_LABEL = {
  homepage: "Homepage",
  onboarding: "Onboarding",
  kern: "Location page (Kern River)",
  sonoma: "Location page (Lake Sonoma)",
  newsHero: "Newsletter — hero",
  newsReports: "Newsletter — content",
};

const SEV = {
  critical: { label: "Critical", color: "#B91C1C" },
  high: { label: "High", color: "#EA580C" },
  medium: { label: "Medium", color: "#CA8A04" },
  low: { label: "Low", color: "#4C1D95" },
};
const EFFORT_SCORE = { S: 22, M: 52, L: 82 };
const IMPACT_SCORE = { critical: 86, high: 64, medium: 40, low: 22 };

const findings = [
  {
    id: "F01", screen: "homepage", severity: "critical", effort: "M", pin: { x: 92, y: 6 },
    desc: "Counties and lakes are buried in the hamburger menu and long scroll.",
    heuristic: "Recognition rather than recall",
    observation: "Core browse pages (counties, lakes) are only reachable via the hamburger menu or by scrolling past the fold; there's no persistent primary or secondary navigation to them.",
    userImpact: "Users can't tell what's available and must remember where things live, so they stop exploring after one or two pages.",
    businessImpact: "Fewer location page views per session directly caps the follows, reports and newsletter signups that depend on those pages.",
    recommendation: "Promote counties and lakes into a persistent nav (a mega-menu or secondary bar) so any water is reachable from anywhere in two clicks.",
    caption: "Look top-right: the only path to counties and lakes is the hamburger — the browse structure is hidden.",
  },
  {
    id: "F02", screen: "homepage", severity: "critical", effort: "S", pin: { x: 50, y: 62 },
    desc: "Red 'alert' styling used on a newsletter banner where there is no error.",
    heuristic: "Match between system and the real world",
    observation: "The newsletter prompt and several labels use error-red, a colour users read as 'something is wrong'.",
    userImpact: "Red creates false urgency and tension on a friendly prompt, so users dismiss it instead of engaging.",
    businessImpact: "A mis-signalled signup banner suppresses newsletter conversion — a primary retention channel.",
    recommendation: "Reserve red for true errors only. Restyle the banner as a positive, branded CTA card with an inviting visual.",
    caption: "Look at the banner colour: red reads as an error, not an invitation to subscribe.",
  },
  {
    id: "F03", screen: "onboarding", severity: "critical", effort: "M", pin: { x: 50, y: 20 },
    desc: "Onboarding under-sells its value; 'Skip' is prominent and 'Continue' is weak.",
    heuristic: "Visibility of system status",
    observation: "The follow step leads with a generic title, gives 'Skip for now' equal weight, and ends on a flat 'Continue to Dashboard' button.",
    userImpact: "New users don't understand why following matters, so they skip the one action that makes the product feel alive on session two.",
    businessImpact: "Skipped follows mean empty dashboards and a second session with nothing to return for — the core activation leak.",
    recommendation: "Lead with the payoff of following, de-emphasize 'Skip', add social proof, and make the primary CTA benefit-driven ('Show my spots').",
    caption: "Compare the two buttons: 'Skip for now' competes with the primary action instead of receding.",
  },
  {
    id: "F04", screen: "homepage", severity: "high", effort: "S", pin: { x: 30, y: 88 },
    desc: "Interactive actions (Comment, Share) have no hover or click affordance.",
    heuristic: "Consistency and standards",
    observation: "Action links on report cards don't change on hover and don't look pressable.",
    userImpact: "Users can't tell what's clickable, so engagement actions go unnoticed.",
    businessImpact: "Lower interaction on reports weakens the community loop that keeps anglers coming back.",
    recommendation: "Give every action a consistent hover state and button styling; make Follow / Comment / Share obviously tappable.",
    caption: "Hover the card actions: nothing responds, so they don't read as interactive.",
  },
  {
    id: "F05", screen: "onboarding", severity: "high", effort: "M", pin: { x: 20, y: 55 },
    desc: "Empty states ('Not following', empty 'My Reports') are passive dead-ends.",
    heuristic: "Help users recognise and recover",
    observation: "Empty states show a bare label with no illustration, benefit copy, or first action.",
    userImpact: "The most teachable moment is wasted; users see a blank and leave without a next step.",
    businessImpact: "Empty dashboards on first visit are a direct driver of one-and-done sessions.",
    recommendation: "Turn each empty state into a prompt: a friendly visual, one line of benefit, and a single clear action ('Follow your first spot').",
    caption: "Look at the follow list: an empty state with no guidance on what to do next.",
  },
  {
    id: "F06", screen: "kern", severity: "high", effort: "S", pin: { x: 88, y: 30 },
    desc: "Multiple competing primary CTAs on the location page.",
    heuristic: "Aesthetic and minimalist design",
    observation: "The location header stacks several equally-weighted buttons, so no single next step stands out.",
    userImpact: "Choice overload — users hesitate instead of taking the key action (log a report / follow).",
    businessImpact: "Diluted CTAs lower the report-submission and follow rates the page exists to drive.",
    recommendation: "Keep one primary CTA ('Log Report'); demote the rest to secondary or icon buttons.",
    caption: "Look at the button row: several primaries compete, so none wins.",
  },
  {
    id: "F07", screen: "newsReports", severity: "high", effort: "M", pin: { x: 50, y: 45 },
    desc: "Newsletter is one dense block — no dividers, summaries, or mid-content CTA.",
    heuristic: "Aesthetic and minimalist design",
    observation: "Sections run together with no visual breaks and a single CTA only at the very end.",
    userImpact: "Readers skim, lose the thread, and never reach the one CTA at the bottom.",
    businessImpact: "Low newsletter click-through means the retention channel underperforms.",
    recommendation: "Add section dividers and one-line summaries, and place a 'Log your report' CTA mid-newsletter, not just at the end.",
    caption: "Scan the layout: sections blur together with no breathing room or in-line CTA.",
  },
  {
    id: "F08", screen: "homepage", severity: "high", effort: "S", pin: { x: 50, y: 34 },
    desc: "Hero value proposition and secondary CTA are weak.",
    heuristic: "Match between system and the real world",
    observation: "The hero leads with a search field but never states what FishCaddy does or offers a clear secondary path.",
    userImpact: "First-time visitors don't grasp the value quickly enough to commit.",
    businessImpact: "A vague hero lowers top-of-funnel conversion into signups and follows.",
    recommendation: "Add a one-line value statement above the search and a clear secondary CTA ('Browse waters').",
    caption: "Read the hero: it jumps to search without saying what the product is for.",
  },
  {
    id: "F09", screen: "newsHero", severity: "high", effort: "S", pin: { x: 18, y: 40 },
    desc: "Newsletter hero uses a checkmark that implies the user is already subscribed.",
    heuristic: "Match between system and the real world",
    observation: "A green checkmark sits next to the subscribe prompt, signalling completion.",
    userImpact: "Users think they're already signed up and don't subscribe.",
    businessImpact: "The misleading signifier suppresses new subscriptions.",
    recommendation: "Replace the checkmark with an inviting visual and a clear, unfinished-state subscribe CTA.",
    caption: "Look at the icon: a checkmark reads as 'done', discouraging signup.",
  },
  {
    id: "F10", screen: "kern", severity: "high", effort: "M", pin: { x: 40, y: 82 },
    desc: "Fishing reports — the page's most valuable content — sit far below the fold.",
    heuristic: "Recognition rather than recall",
    observation: "Long description and widgets push community reports well down the page.",
    userImpact: "Users miss the freshest, most useful content and bounce.",
    businessImpact: "Buried reports reduce engagement and repeat visits to location pages.",
    recommendation: "Lift recent reports higher; collapse the long description behind 'Read more'.",
    caption: "Note the order: static description dominates while live reports are pushed down.",
  },
  {
    id: "F11", screen: "homepage", severity: "medium", effort: "M", pin: { x: 50, y: 80 },
    desc: "'Recently Stocked Waters' and 'Latest Fishing Reports' use near-identical cards.",
    heuristic: "Consistency and standards",
    observation: "Two different content types share the same card layout back-to-back.",
    userImpact: "Cognitive fatigue — users can't tell the sections apart at a glance.",
    businessImpact: "Blurred sections lower scanning efficiency and engagement with each.",
    recommendation: "Differentiate the two sections with distinct card treatments, headers, or layout.",
    caption: "Compare the two card rows: same layout, different purpose — they blend together.",
  },
  {
    id: "F12", screen: "kern", severity: "medium", effort: "S", pin: { x: 30, y: 40 },
    desc: "Long location description has no truncation or 'Read more'.",
    heuristic: "Aesthetic and minimalist design",
    observation: "A multi-paragraph description renders in full at the top of the page.",
    userImpact: "Wall of text delays users from reaching the interactive content.",
    businessImpact: "Slower time-to-value on the highest-traffic page type.",
    recommendation: "Truncate to two lines with a 'Read more' toggle.",
    caption: "Look at the intro copy: it runs long before anything actionable appears.",
  },
  {
    id: "F13", screen: "sonoma", severity: "medium", effort: "M", pin: { x: 78, y: 60 },
    desc: "Section titles and side widgets are inconsistent across tabs.",
    heuristic: "Consistency and standards",
    observation: "'Nearby Lakes & Rivers' and utility widgets use different titling and styling from the rest of the page.",
    userImpact: "The page feels bolted together, eroding trust in the data.",
    businessImpact: "Inconsistency undermines perceived credibility of a data product.",
    recommendation: "Standardise section titles and widget styling across all tabs.",
    caption: "Compare titles across the page: the nearby-waters block doesn't match the system.",
  },
  {
    id: "F14", screen: "onboarding", severity: "medium", effort: "S", pin: { x: 80, y: 40 },
    desc: "The follow list has no sorting or filtering.",
    heuristic: "Flexibility and efficiency of use",
    observation: "Spots appear in a fixed order with no way to sort by distance or region.",
    userImpact: "Users can't quickly find their local waters to follow.",
    businessImpact: "Harder discovery means fewer follows completed during onboarding.",
    recommendation: "Add simple sort/filter (by region or distance) to the follow step.",
    caption: "Note the list controls: there's no way to sort to your area.",
  },
  {
    id: "F15", screen: "kern", severity: "medium", effort: "L", pin: { x: 88, y: 62 },
    desc: "Map and utility widgets aren't standardised into a reusable pattern.",
    heuristic: "Consistency and standards",
    observation: "Weather, bite-meter and map widgets each use different framing and spacing.",
    userImpact: "Scanning the side rail takes longer than it should.",
    businessImpact: "Ad-hoc widgets slow future page work and dilute the brand system.",
    recommendation: "Define one widget pattern (header, padding, states) and apply it across the rail.",
    caption: "Look at the right rail: each widget is framed differently.",
  },
];

const roadmap = [
  { col: "Ship this week", ids: ["F02", "F04", "F06", "F08", "F09"], why: "High-impact, low-effort quick wins — CTA and colour fixes that lift engagement with no rebuild." },
  { col: "Next sprint", ids: ["F01", "F03", "F05", "F07", "F10"], why: "The activation core — navigation, onboarding, empty states and content order that drive the second session." },
  { col: "Backlog", ids: ["F11", "F12", "F13", "F14", "F15"], why: "Consistency and polish — real gains, but sequenced after the leaks that cost the most." },
];

const measures = [
  { id: "F03", metric: "Onboarding completion rate", hyp: "Leading with value and de-emphasizing 'Skip' increases the share of new users who follow at least one spot." },
  { id: "F01", metric: "Location pages per session", hyp: "Surfacing counties and lakes in navigation increases how many waters a visitor views per session." },
  { id: "F05", metric: "First-report submission rate", hyp: "Actionable empty states raise the share of new users who follow a spot and submit their first report." },
  { id: "F07", metric: "Newsletter click-through rate", hyp: "Dividers, summaries and a mid-newsletter CTA increase clicks back into the product from email." },
];

function SevChip({ severity }) {
  const s = SEV[severity];
  return (
    <span
      className="inline-flex items-center gap-1.5 label-mono text-white px-2 py-0.5"
      style={{ background: s.color }}
    >
      {s.label}
    </span>
  );
}
function EffortChip({ effort }) {
  return (
    <span className="inline-flex items-center label-mono border border-[#0A0A0A] px-2 py-0.5">
      {effort} effort
    </span>
  );
}
function Pin({ n, color }) {
  return (
    <span
      className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full text-white font-display text-sm border-2 border-white shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
      style={{ background: color }}
    >
      {n}
    </span>
  );
}

function Matrix() {
  const [active, setActive] = useState(null);
  return (
    <div className="relative w-full max-w-[560px] mx-auto lg:mx-0 aspect-square border border-[#0A0A0A] bg-white">
      {/* quadrant lines */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#0A0A0A]/20" />
      <div className="absolute top-1/2 left-0 right-0 h-px bg-[#0A0A0A]/20" />
      {/* quadrant labels */}
      <span className="absolute top-2 left-2 label-mono text-[#4C1D95]">Quick wins</span>
      <span className="absolute top-2 right-2 label-mono text-[#595959]">Major projects</span>
      <span className="absolute bottom-2 left-2 label-mono text-[#595959]">Fill-ins</span>
      <span className="absolute bottom-2 right-2 label-mono text-[#595959]">Reconsider</span>
      {/* axes */}
      <span className="absolute -left-1 top-1/2 -translate-y-1/2 -rotate-90 origin-center label-mono text-[#8A8A8A] hidden md:block">
        Impact →
      </span>
      <span className="absolute bottom-[-22px] left-1/2 -translate-x-1/2 label-mono text-[#8A8A8A]">
        Effort →
      </span>
      {findings.map((f) => {
        const left = EFFORT_SCORE[f.effort];
        const top = 100 - IMPACT_SCORE[f.severity];
        return (
          <button
            key={f.id}
            type="button"
            onMouseEnter={() => setActive(f.id)}
            onMouseLeave={() => setActive((a) => (a === f.id ? null : a))}
            onFocus={() => setActive(f.id)}
            onClick={() => setActive(f.id)}
            className="absolute -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white cursor-pointer transition-transform hover:scale-125 focus:scale-125 outline-none"
            style={{ left: `${left}%`, top: `${top}%`, background: SEV[f.severity].color }}
            data-testid={`matrix-dot-${f.id}`}
            aria-label={`${f.id}: ${f.desc}`}
          >
            {active === f.id && (
              <span className="absolute left-1/2 -translate-x-1/2 bottom-5 whitespace-nowrap bg-[#0A0A0A] text-white label-mono px-2 py-1 z-10 pointer-events-none max-w-[240px] truncate">
                {f.id} · {f.desc}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function FishCaddyAudit() {
  const critical = findings.filter((f) => f.severity === "critical").length;
  const high = findings.filter((f) => f.severity === "high").length;
  const medium = findings.filter((f) => f.severity === "medium").length;

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]" data-testid="case-study-page">
      <SubNav />
      <main>
        {/* Hero */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
              <p className="label-mono text-[#595959]">/ 04 — Case study</p>
              <p className="label-mono text-[#595959]">FishCaddy</p>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter">
              FishCaddy — UX Audit
            </h1>
            <p className="font-display text-2xl md:text-3xl tracking-tighter text-[#4C1D95] mt-4 max-w-3xl">
              A heuristic audit of a fishing-report platform — every finding logged, ranked, and sequenced.
            </p>
            <p className="max-w-3xl text-lg md:text-xl leading-relaxed mt-8 text-[#0A0A0A]">
              FishCaddy helps anglers track stockings, log reports, and follow their favorite waters. I reviewed the key user-facing surfaces against established usability heuristics, logged each issue with a severity and effort estimate, and turned the result into a prioritized plan a small team can act on this week.
            </p>
          </div>
        </section>

        {/* 1 — Audit summary bar */}
        <section className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white" data-testid="audit-summary">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-12 grid grid-cols-2 md:grid-cols-5 gap-y-8">
            {[
              { n: "6", l: "Screens reviewed" },
              { n: "15", l: "Findings logged" },
              { n: `${critical} / ${high} / ${medium}`, l: "Critical / High / Medium" },
              { n: "Heuristic", l: "Review method (Nielsen's 10)" },
              { n: "1 week", l: "Timeframe" },
            ].map((s) => (
              <div key={s.l} className="px-2">
                <div className="font-display text-3xl md:text-4xl tracking-tighter">{s.n}</div>
                <div className="label-mono text-[#DDD6FE] mt-2 max-w-[18ch]">{s.l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* 2 — Scope & method */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 01 — Scope &amp; method</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tighter mb-5">What was reviewed</h2>
                <ul className="space-y-3 text-base md:text-lg leading-relaxed text-[#3F3F46]">
                  {[
                    "Landing / homepage — hero, stocked waters, latest reports",
                    "Onboarding — the follow-your-spots flow and empty states",
                    "Dashboard & navigation — global nav and content discovery",
                    "Location pages — Kern River & Lake Sonoma, tabs and widgets",
                    "Newsletter — hero prompt and weekly digest content",
                    "Coverage: desktop web (primary), responsive breakpoints spot-checked",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-[#0A0A0A] shrink-0 mt-2.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tighter mb-5">How it was reviewed</h2>
                <p className="text-base md:text-lg leading-relaxed text-[#3F3F46] mb-6">
                  Heuristic evaluation against Nielsen&apos;s 10 usability heuristics. Each finding is rated on a defined severity scale:
                </p>
                <ul className="space-y-2.5 mb-6">
                  {[
                    ["critical", "Blocks the task"],
                    ["high", "Causes drop-off"],
                    ["medium", "Adds friction"],
                    ["low", "Polish"],
                  ].map(([k, d]) => (
                    <li key={k} className="flex items-center gap-3">
                      <SevChip severity={k} />
                      <span className="text-base leading-relaxed text-[#3F3F46]">{d}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm leading-relaxed text-[#595959] border-l-2 border-[#4C1D95] pl-4">
                  Not in scope: no moderated user testing and no analytics access — findings are expert-evaluation hypotheses, not behavioural proof. Where a fix is testable, I&apos;ve named the metric that would confirm it (see &ldquo;What I&apos;d measure&rdquo;).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3 — Findings index */}
        <section className="border-b border-[#0A0A0A] bg-[#F5F3FF]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 02 — Findings index</p>
            {/* Desktop table */}
            <table className="hidden md:table w-full border-t border-l border-[#0A0A0A] text-left">
              <thead>
                <tr className="label-mono text-[#595959]">
                  {["ID", "Screen", "Finding", "Heuristic", "Severity", "Effort"].map((h) => (
                    <th key={h} className="border-b border-r border-[#0A0A0A] p-3 align-top bg-white">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {findings.map((f) => (
                  <tr key={f.id} className="align-top hover:bg-white transition-colors">
                    <td className="border-b border-r border-[#0A0A0A] p-3">
                      <a href={`#${f.id}`} className="font-display text-lg text-[#4C1D95] hover:underline">{f.id}</a>
                    </td>
                    <td className="border-b border-r border-[#0A0A0A] p-3 text-sm">{SCREEN_LABEL[f.screen]}</td>
                    <td className="border-b border-r border-[#0A0A0A] p-3 text-sm max-w-[420px]">{f.desc}</td>
                    <td className="border-b border-r border-[#0A0A0A] p-3 text-xs text-[#595959]">{f.heuristic}</td>
                    <td className="border-b border-r border-[#0A0A0A] p-3"><SevChip severity={f.severity} /></td>
                    <td className="border-b border-r border-[#0A0A0A] p-3"><EffortChip effort={f.effort} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {findings.map((f) => (
                <a key={f.id} href={`#${f.id}`} className="block border border-[#0A0A0A] bg-white p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display text-xl text-[#4C1D95]">{f.id}</span>
                    <div className="flex gap-2"><SevChip severity={f.severity} /><EffortChip effort={f.effort} /></div>
                  </div>
                  <p className="text-sm mb-1">{f.desc}</p>
                  <p className="label-mono text-[#595959]">{SCREEN_LABEL[f.screen]} · {f.heuristic}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* 4 — Finding detail blocks */}
        <section className="border-b border-[#0A0A0A]" data-testid="finding-details">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-16 md:pt-24">
            <p className="label-mono text-[#4C1D95]">/ 03 — Findings in detail</p>
          </div>
          {findings.map((f, i) => (
            <div
              key={f.id}
              id={f.id}
              className="scroll-mt-20 max-w-[1440px] mx-auto px-4 md:px-8 py-14 md:py-20 border-b border-[#0A0A0A]/10 last:border-0"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
                {/* Screenshot with pin */}
                <figure className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative border border-[#0A0A0A] bg-white overflow-hidden">
                    <img src={SCREENS[f.screen]} alt={`${SCREEN_LABEL[f.screen]} — ${f.desc}`} loading="lazy" className="w-full h-auto block" />
                    <span className="absolute" style={{ left: `${f.pin.x}%`, top: `${f.pin.y}%` }}>
                      <Pin n={f.id.replace("F", "")} color={SEV[f.severity].color} />
                    </span>
                  </div>
                  <figcaption className="text-sm text-[#595959] leading-relaxed mt-3 font-mono-ibm">
                    {SCREEN_LABEL[f.screen]} — {f.caption}
                  </figcaption>
                </figure>

                {/* Four labelled parts */}
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-display text-3xl tracking-tighter text-[#4C1D95]">{f.id}</span>
                    <SevChip severity={f.severity} />
                    <EffortChip effort={f.effort} />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl tracking-tighter mb-6">{f.desc}</h3>
                  <div className="space-y-5">
                    <div>
                      <p className="label-mono text-[#0A0A0A] mb-1.5">Observation</p>
                      <p className="text-base leading-relaxed text-[#52525B]">{f.observation}</p>
                    </div>
                    <div>
                      <p className="label-mono text-[#0A0A0A] mb-1.5">Why it matters</p>
                      <p className="text-base leading-relaxed text-[#52525B]"><span className="text-[#0A0A0A] font-semibold">User: </span>{f.userImpact}</p>
                      <p className="text-base leading-relaxed text-[#52525B] mt-2"><span className="text-[#0A0A0A] font-semibold">Business: </span>{f.businessImpact}</p>
                    </div>
                    <div>
                      <p className="label-mono text-[#0A0A0A] mb-1.5">Recommendation</p>
                      <p className="text-base leading-relaxed text-[#0A0A0A]">{f.recommendation}</p>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <span className="label-mono text-[#595959]">Heuristic:</span>
                      <span className="label-mono text-[#4C1D95]">{f.heuristic}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* 5 — Impact vs effort matrix */}
        <section className="border-b border-[#0A0A0A] bg-[#F5F3FF]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 04 — Impact vs. effort</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-display text-3xl md:text-5xl tracking-tighter mb-6">Where to start<span className="text-[#4C1D95]">.</span></h2>
                <p className="text-lg leading-relaxed text-[#3F3F46] mb-6 max-w-md">
                  Every finding plotted by expected impact against effort to fix. Hover or tap a dot to see the finding. The top-left quadrant — high impact, low effort — is where FishCaddy should start.
                </p>
                <div className="flex flex-wrap gap-3">
                  {Object.keys(SEV).map((k) => (
                    <span key={k} className="inline-flex items-center gap-2 label-mono text-[#595959]">
                      <span className="w-3 h-3 rounded-full" style={{ background: SEV[k].color }} />
                      {SEV[k].label}
                    </span>
                  ))}
                </div>
              </div>
              <Matrix />
            </div>
          </div>
        </section>

        {/* 6 — Prioritised roadmap */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#4C1D95] mb-8">/ 05 — Prioritised roadmap</p>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#0A0A0A]">
              {roadmap.map((r) => (
                <div key={r.col} className="border-b border-r border-[#0A0A0A] p-6 md:p-8 flex flex-col">
                  <h3 className="font-display text-2xl tracking-tighter mb-4">{r.col}</h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {r.ids.map((id) => (
                      <a key={id} href={`#${id}`} className="font-display text-lg text-[#4C1D95] hover:underline border border-[#4C1D95]/30 px-2">{id}</a>
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed text-[#52525B]">{r.why}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 7 — What I'd measure */}
        <section className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#A78BFA] mb-8">/ 06 — What I&apos;d measure</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter mb-4 max-w-3xl">
              Framed as hypotheses, not promises.
            </h2>
            <p className="text-[#DDD6FE] leading-relaxed max-w-2xl mb-12">
              Without analytics access I can&apos;t claim outcomes — but each priority fix maps to a metric that would prove whether it worked.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/20">
              {measures.map((m) => (
                <div key={m.id} className="border-b border-r border-white/20 p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <a href={`#${m.id}`} className="font-display text-xl text-[#A78BFA] hover:underline">{m.id}</a>
                    <span className="label-mono text-white">{m.metric}</span>
                  </div>
                  <p className="text-sm md:text-base leading-relaxed text-[#DDD6FE]">{m.hyp}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 8 — Reflection */}
        <section className="border-b border-[#0A0A0A] bg-[#4C1D95] text-white">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28">
            <p className="label-mono mb-6 text-[#DDD6FE]">/ 07 — Reflection</p>
            <p className="font-display text-3xl md:text-5xl tracking-tighter max-w-4xl leading-[1.05]">
              A good audit isn&apos;t a list of opinions — it&apos;s a ranked, honest read on where a product loses people and what it costs. FishCaddy didn&apos;t need a redesign; it needed the leaks named and prioritised.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/#contact" className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-4 label-mono hover:bg-white hover:text-[#0A0A0A] transition-colors" data-testid="fc-cta-book">
                Book your audit <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/#work" className="inline-flex items-center gap-2 border border-white px-6 py-4 label-mono hover:bg-white hover:text-[#4C1D95] transition-colors" data-testid="fc-cta-back">
                <ArrowLeft size={16} strokeWidth={1.5} /> See more work
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
